import { FEATURE_FLAGS_HEADER, type FeatureFlagOverrides, parseFeatureFlagOverrides } from "@unbox/api";
import { useSyncExternalStore } from "react";
import { RESOLVES_FLAGS_LOCALLY } from "@/utils/feature-flag-source";

// A JSON object, e.g. {"F-creator-tags": true, "F-ask-unbox": false}. e2e tests set it via addInitScript.
const STORAGE_KEY = "feature_flags";

const listeners = new Set<() => void>();
let snapshot: { raw: string | null; overrides: FeatureFlagOverrides } = { raw: null, overrides: {} };

const readRaw = (): string | null => {
  if (import.meta.env.VITE_STAGE === "prod") return null;
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage can be unavailable (private mode, blocked site data); treat it as nothing set.
    return null;
  }
};

/** Returns the same object until the stored value changes, as `useSyncExternalStore` requires. */
export const readFeatureFlagOverrides = (): FeatureFlagOverrides => {
  const raw = readRaw();
  if (raw !== snapshot.raw) snapshot = { raw, overrides: parseFeatureFlagOverrides(raw) };
  return snapshot.overrides;
};

export const writeFeatureFlagOverrides = (overrides: FeatureFlagOverrides): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  for (const listener of listeners) listener();
};

/** Sends the overrides to the API, which resolves its own flag checks from them. */
export const featureFlagHeaders = (): Record<string, string> =>
  RESOLVES_FLAGS_LOCALLY ? { [FEATURE_FLAGS_HEADER]: JSON.stringify(readFeatureFlagOverrides()) } : {};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useFeatureFlagOverrides = (): FeatureFlagOverrides =>
  useSyncExternalStore(subscribe, readFeatureFlagOverrides);
